/**
 * Screen 26 — Notifications centre.
 *
 * Lists every notification the centre received (newest first), grouped
 * into "New" (unread) and "Earlier" (read). Per-row chips render an icon
 * keyed off the type registry the server returns. Inline actions fall
 * back to deep-link navigation when none apply.
 *
 * The list is paginated forward (`before=` cursor); on initial load we
 * fetch the first page. The bell badge stays in sync via the singleton
 * notification bus — any new `notification.new` event while this page is
 * open prepends to the list without a reload.
 */

import { api, ApiError } from '../api.js';
import { I, icon } from '../components/icons.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import {
  decrementUnreadOptimistically,
  fetchNotifications,
  markAllRead,
  markRead,
  onNotifications,
  setUnreadCountFromServer,
} from '../lib/notifications.js';
import {
  isAndroid,
  isIOS,
  isPushSupported,
  isStandalonePWA,
  permissionState,
  requestPermissionAndSubscribe,
} from '../lib/push.js';

let _state = null;
let _unsubBus = null;

export async function renderNotifications(root) {
  _state = { items: [], nextBefore: null, loading: true, error: false };
  root.innerHTML = shell();
  wireEnablePush();
  paint();

  await loadFirstPage();
  paint();

  // Live updates: when emit() lands on the server, the WS pushes a
  // notification.new event which the singleton broadcasts. We prepend
  // it so the centre stays warm while open.
  _unsubBus = onNotifications((event) => {
    if (event.kind === 'new' && event.notification) {
      _prepend(event.notification);
    }
  });

  window.addEventListener('popstate', _teardown, { once: true });
}

function _teardown() {
  if (_unsubBus) {
    _unsubBus();
    _unsubBus = null;
  }
}

function shell() {
  return `
    <div class="appbar">
      <a href="/" data-link class="back" aria-label="Back" style="text-decoration:none;color:inherit;">
        ${icon(I.back, { size: 18 })}
      </a>
      <div class="t">Notifications</div>
      <div class="sp"></div>
      <a href="/notifications/prefs" data-link aria-label="Notification settings"
         style="margin-right:6px;color:inherit;text-decoration:none;display:inline-flex;
                width:30px;height:30px;align-items:center;justify-content:center;">
        ${icon(I.cog, { size: 18 })}
      </a>
      <span id="notif-mark-all" class="tag vio" role="button" tabindex="0"
            style="cursor:pointer;user-select:none;">Mark all read</span>
    </div>
    <div class="pad" id="notif-body">
      <div id="notif-enable-push-slot">${enablePushCardHtml()}</div>
      <div id="notif-list">
        <div class="skel"></div>
        <div class="skel"></div>
        <div class="skel"></div>
      </div>
    </div>
  `;
}

async function loadFirstPage() {
  try {
    const res = await fetchNotifications({ limit: 30 });
    _state.items = res.items || [];
    _state.nextBefore = res.next_before || null;
    _state.loading = false;
    _state.error = false;
    // Prime the global unread count from the server's authoritative
    // value (the WS snapshot may not yet have landed on a cold visit).
    if (typeof res.unread_count === 'number') {
      setUnreadCountFromServer(res.unread_count);
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    _state.loading = false;
    _state.error = true;
    toast.fromError(err, 'Could not load notifications.');
  }
}

function _prepend(notification) {
  // De-dup if a server retry produces the same row twice (rare but
  // possible across reconnects). Keep newest-first.
  if (!notification || !notification.id) return;
  _state.items = [
    notification,
    ..._state.items.filter((n) => n.id !== notification.id),
  ];
  paint();
}

// ----- rendering ------------------------------------------------------------


function paint() {
  const list = document.getElementById('notif-list');
  if (!list) return;
  if (_state.loading) return;

  // "Mark all read" stays visible per the screen-26 wireframe; we dim
  // it when there's nothing unread so a tap is a no-op-safe affordance
  // instead of an active control. Push card stays in its slot above
  // regardless of how the list renders.
  const newItems = _state.items.filter((n) => !n.read_at);
  const earlier = _state.items.filter((n) => !!n.read_at);
  applyMarkAllAffordance(newItems.length);

  if (_state.error) {
    list.innerHTML = `<div class="card muted">Could not load — pull to refresh.</div>`;
    return;
  }
  if (!_state.items.length) {
    list.innerHTML = emptyStateHtml();
    return;
  }

  list.innerHTML = `
    ${newItems.length ? `<h3 class="sec"><span class="dot"></span>New</h3>${newItems.map(rowHtml).join('')}` : ''}
    ${earlier.length ? `<h3 class="sec">Earlier</h3>${earlier.map(rowHtml).join('')}` : ''}
  `;
  wireListInteractions();
  wireMarkAll();
}

function applyMarkAllAffordance(unreadCount) {
  // Always visible (matches the wireframe). Dimmed + non-interactive
  // when there's nothing to clear so an accidental tap doesn't fire
  // an empty mark-all-read round-trip.
  const el = document.getElementById('notif-mark-all');
  if (!el) return;
  if (unreadCount > 0) {
    el.style.opacity = '1';
    el.style.cursor = 'pointer';
    el.removeAttribute('aria-disabled');
  } else {
    el.style.opacity = '0.45';
    el.style.cursor = 'default';
    el.setAttribute('aria-disabled', 'true');
  }
}

function splitByRead(items) {
  const newItems = [];
  const earlier = [];
  for (const it of items) {
    (it.read_at ? earlier : newItems).push(it);
  }
  return { newItems, earlier };
}

function emptyStateHtml() {
  return `
    <div class="card" style="padding:18px;text-align:center;color:var(--mute);">
      <div style="font-size:13px;font-weight:600;color:#fff;margin-bottom:4px;">
        Nothing here yet
      </div>
      <div style="font-size:11.5px;">
        Friend requests, duel invites, and live-trivia reminders will land here.
      </div>
    </div>
  `;
}

function rowHtml(n) {
  const iconSvg = iconForType(n.icon, n.type);
  const iconWrap = `<div class="ic" style="${iconWrapStyle(n.type)}">${iconSvg}</div>`;
  const when = fmtRelative(n.created_at);
  const subBits = [escape(subtitleFor(n))];
  if (when) subBits.push(escape(when));
  const subtitle = subBits.filter(Boolean).join(' · ');
  return `
    <div class="card" data-notification-id="${escape(n.id)}"
         data-deeplink="${escape(n.deeplink || '')}"
         style="padding:12px;${n.read_at ? 'opacity:0.74;' : ''}">
      <div class="row">
        ${iconWrap}
        <div class="grow" style="min-width:0;">
          <b style="font-size:13px;color:#fff;font-weight:600;display:block;">
            ${escape(n.title)}
          </b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:1px;">
            ${subtitle}
          </div>
        </div>
        ${rightSlotHtml(n)}
      </div>
      ${actionsHtml(n)}
    </div>
  `;
}

function iconWrapStyle(type) {
  // Background + accent colours follow the wireframe palette:
  // duel cluster → teal, friend → violet, live trivia → pink, eraser →
  // gold, duel_result trophy → gold.
  switch (type) {
    case 'duel_challenge_received':
    case 'duel_host_joined':
    case 'duel_accepted':
    case 'duel_declined':
    case 'duel_public_joined':
    case 'duel_your_turn':
      return 'background:#0F352A;color:#5FE0BE;';
    case 'duel_result':
    case 'duel_result_async':
    case 'duel_forfeit_win':
      return 'background:#3A2E12;color:#F7C667;';
    case 'duel_turn_reminder':
    case 'duel_expired_refund':
      return 'background:#3A2A12;color:#F7C667;';
    case 'live_trivia_reminder':
    case 'live_trivia_announce':
      return 'background:#3A1528;color:#FF8FB6;';
    case 'friend_request_received':
    case 'friend_request_accepted':
      return 'background:#251E48;color:#B7A6FF;';
    case 'eraser_awarded':
      return 'background:#3A2A12;color:#F7C667;';
    case 'fiftyfifty_awarded':
    case 'fiftyfifty_referral_earned':
      // 50/50 brand pink — matches the live-room HUD + invite page accent.
      return 'background:#3A1528;color:#FF8FB6;';
    case 'message_received':
    case 'message_request_received':
    case 'moderation_warning':
      // Chat teal — matches the chat-icon accent on Home.
      return 'background:#0F352A;color:#5FE0BE;';
    default:
      return 'background:#251E48;color:#B7A6FF;';
  }
}

function iconForType(iconKey, type) {
  // Server-supplied icon key takes precedence; if it doesn't map cleanly,
  // fall back to the type-derived guess and then the bell.
  const explicit = ICON_MAP[iconKey];
  if (explicit) return icon(explicit, { size: 16 });
  const guess = ICON_MAP[guessIconKey(type)];
  return icon(guess || I.bell, { size: 16 });
}

const ICON_MAP = {
  bell: I.bell,
  friend: I.friends,
  swords: I.swords,
  trophy: I.trophy,
  eraser: I.eraser,
  fiftyfifty: I.fiftyfifty,
  chat: I.chat,
  warning: I.bell,
};

function guessIconKey(type) {
  if (!type) return 'bell';
  if (type.startsWith('friend_')) return 'friend';
  if (type === 'duel_result') return 'trophy';
  if (type.startsWith('duel_')) return 'swords';
  if (type.startsWith('live_trivia')) return 'bell';
  if (type === 'eraser_awarded') return 'eraser';
  if (type.startsWith('fiftyfifty')) return 'fiftyfifty';
  // Phase 8.12 — chat glyph for both messaging notification types.
  if (type === 'message_received' || type === 'message_request_received') {
    return 'chat';
  }
  return 'bell';
}

function rightSlotHtml(n) {
  // Single-tap deeplink chip for types that don't ship inline actions —
  // currently the live-trivia reminder and the friend-accepted "Challenge"
  // shortcut share this slot.
  if (n.type === 'live_trivia_reminder') {
    return `<span class="btn pink sm" data-deeplink-go style="margin:0;">Join</span>`;
  }
  if (n.type === 'friend_request_accepted') {
    return `<span class="btn ghost sm" data-deeplink-go style="margin:0;">Challenge</span>`;
  }
  return '';
}

function actionsHtml(n) {
  // Inline action buttons only render for UNREAD rows. Once the user
  // has engaged with a notification (tapped an inline action, tapped
  // the row to navigate, or used mark-all-read), the underlying
  // duel/friend-request is either resolved or no longer the user's
  // primary action surface — the friends page is. Keeping stale
  // Accept/Later buttons in the "Earlier" section just invites taps
  // into 4xx errors. The row itself stays clickable so anyone who
  // wants to revisit can still deep-link through.
  if (n.read_at) return '';
  if (n.type === 'duel_challenge_received') {
    const challengeId = n.data?.challenge_id;
    if (!challengeId) return '';
    return `
      <div class="row" style="gap:8px;margin-top:9px;">
        <span class="btn teal sm" data-challenge-action="accept"
              data-challenge-id="${escape(challengeId)}"
              style="flex:1;width:auto;margin:0;">Accept</span>
        <span class="btn ghost sm" data-challenge-action="later"
              data-deeplink-go
              style="flex:1;width:auto;margin:0;">Later</span>
      </div>
    `;
  }
  if (n.type === 'friend_request_received') {
    const requestId = n.data?.request_id;
    if (!requestId) return '';
    return `
      <div class="row" style="gap:8px;margin-top:9px;">
        <span class="btn teal sm" data-friend-action="accept"
              data-request-id="${escape(String(requestId))}"
              style="flex:1;width:auto;margin:0;">Accept</span>
        <span class="btn ghost sm" data-friend-action="decline"
              data-request-id="${escape(String(requestId))}"
              style="flex:1;width:auto;margin:0;">Decline</span>
      </div>
    `;
  }
  return '';
}

function subtitleFor(n) {
  // Compact second-line summary derived from the type-specific data
  // payload. Falls back to body when nothing structured fits.
  const d = n.data || {};
  if (n.type === 'duel_challenge_received') {
    const stake = Number(d.stake_kobo || 0);
    return stake > 0 ? `Staked ₦${(stake / 100).toLocaleString()}` : 'No stake';
  }
  if (n.type === 'live_trivia_reminder') {
    // Mirrors the backend's whole-hour rendering (see push.py title
    // composition): 1440 → "24 hours", 60 → "1 hour", 5 → "5 min".
    // Without this, a day-before reminder would show "1440 min" which
    // is technically correct but reads as a bug.
    const m = Number(d.minutes_before || 0);
    if (m <= 0) return '';
    if (m >= 60 && m % 60 === 0) {
      const h = m / 60;
      return `Starts in ${h} hour${h === 1 ? '' : 's'}`;
    }
    return `Starts in ${m} min`;
  }
  return n.body || '';
}

// ----- timestamp formatting -------------------------------------------------

function fmtRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const ms = Date.now() - d.getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
}

// ----- interactions ---------------------------------------------------------

function wireMarkAll() {
  const el = document.getElementById('notif-mark-all');
  if (!el) return;
  el.onclick = async () => {
    // No-op when dimmed (nothing unread). Saves a wasted round-trip
    // and matches the visual affordance.
    if (el.getAttribute('aria-disabled') === 'true') return;
    try {
      await markAllRead();
    } catch (err) {
      toast.fromError(err, 'Could not mark all read.');
      return;
    }
    const now = new Date().toISOString();
    _state.items = _state.items.map((n) => (n.read_at ? n : { ...n, read_at: now }));
    setUnreadCountFromServer(0);
    paint();
  };
  el.onkeydown = (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      el.click();
    }
  };
}

function wireListInteractions() {
  const list = document.getElementById('notif-list');
  if (!list) return;
  list.querySelectorAll('[data-notification-id]').forEach((row) => {
    const id = row.dataset.notificationId;
    const deeplink = row.dataset.deeplink || '';
    // Inline actions take precedence over a row-level tap; we wire them
    // first and stopPropagation so they don't double-fire navigation.
    row.querySelectorAll('[data-challenge-action]').forEach((el) => {
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const action = el.dataset.challengeAction;
        const challengeId = el.dataset.challengeId;
        if (action === 'accept') {
          handleChallengeAccept(id, challengeId);
        }
      });
    });
    row.querySelectorAll('[data-friend-action]').forEach((el) => {
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const action = el.dataset.friendAction;
        const requestId = Number(el.dataset.requestId);
        if (action === 'accept') {
          handleFriendAccept(id, requestId);
        } else if (action === 'decline') {
          handleFriendDecline(id, requestId);
        }
      });
    });
    row.querySelectorAll('[data-deeplink-go]').forEach((el) => {
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        markAndNavigate(id, deeplink || '/');
      });
    });
    // Tapping the row itself marks-read + navigates.
    row.addEventListener('click', () => {
      markAndNavigate(id, deeplink || '/');
    });
  });
}

async function markAndNavigate(id, deeplink) {
  // Optimistically mark the row read so the UI updates before the
  // network round-trip completes. The server is authoritative — if
  // the call fails we just don't surface an error (the row stays
  // visually-read but the unread count will reconcile on next snapshot).
  const row = _state.items.find((n) => n.id === id);
  if (row && !row.read_at) {
    row.read_at = new Date().toISOString();
    decrementUnreadOptimistically();
    paint();
    markRead(id).catch(() => {});
  }
  // Deep-link is a frontend route. Empty deeplinks fall back to /.
  navigate(deeplink || '/');
}

async function handleChallengeAccept(notificationId, challengeId) {
  try {
    const ch = await api(`/challenges/${encodeURIComponent(challengeId)}/accept`, {
      method: 'POST',
    });
    await silentMark(notificationId);
    if (ch?.match_id) {
      navigate(`/match/${ch.match_id}`);
    } else {
      navigate('/friends?tab=challenges');
    }
  } catch (err) {
    // Stale notifications are common: a duel that's already played out,
    // been cancelled, or expired will 4xx on accept. Anything that isn't
    // a fresh-and-actionable challenge → drop the card so the user
    // doesn't keep tapping into an error.
    if (_isStaleApiError(err)) {
      _resolveStaleRow(notificationId, _staleMessage(err, 'This duel is no longer active.'));
      return;
    }
    toast.fromError(err, 'Could not accept the duel.');
  }
}

async function handleFriendAccept(notificationId, requestId) {
  try {
    await api(`/friends/requests/${requestId}/accept`, { method: 'POST' });
    await silentMark(notificationId);
    toast.success('Friend request accepted.');
    // Remove the row so the centre reflects that there's nothing to act
    // on anymore.
    _state.items = _state.items.filter((n) => n.id !== notificationId);
    paint();
  } catch (err) {
    if (_isStaleApiError(err)) {
      _resolveStaleRow(notificationId, _staleMessage(err, 'This friend request is no longer pending.'));
      return;
    }
    toast.fromError(err, 'Could not accept the friend request.');
  }
}

async function handleFriendDecline(notificationId, requestId) {
  try {
    await api(`/friends/requests/${requestId}/decline`, { method: 'POST' });
    await silentMark(notificationId);
    _state.items = _state.items.filter((n) => n.id !== notificationId);
    paint();
  } catch (err) {
    if (_isStaleApiError(err)) {
      _resolveStaleRow(notificationId, _staleMessage(err, 'This friend request is no longer pending.'));
      return;
    }
    toast.fromError(err, 'Could not decline the friend request.');
  }
}

// ----- stale-row handling ---------------------------------------------------

function _isStaleApiError(err) {
  if (!(err instanceof ApiError)) return false;
  // 404 + 409/410/422 mean the underlying entity is gone or has moved
  // past `pending`. 500 in this category usually surfaces the same
  // root cause (e.g. a re-accept that tries to mint a second match)
  // — drop the card and inform rather than dump a raw error toast on
  // the user.
  return [404, 409, 410, 422, 500].includes(err.status);
}

function _staleMessage(err, fallback) {
  // Prefer the API's user-facing message when it gave us one; otherwise
  // surface a generic copy. Avoids leaking technical jargon ("challenge
  // already accepted") when we already have a friendlier alternative.
  if (err && typeof err.message === 'string' && err.message && err.status !== 500) {
    return err.message;
  }
  return fallback;
}

function _resolveStaleRow(notificationId, message) {
  // Mark read on the server so the unread count drops, drop the row
  // client-side so the inline actions can't be tapped again, then
  // surface a friendly toast.
  void silentMark(notificationId);
  _state.items = _state.items.filter((n) => n.id !== notificationId);
  paint();
  toast.info(message);
}

async function silentMark(id) {
  const row = _state.items.find((n) => n.id === id);
  if (row && !row.read_at) {
    row.read_at = new Date().toISOString();
    decrementUnreadOptimistically();
  }
  try {
    await markRead(id);
  } catch {
    /* best-effort */
  }
}

// ----- enable-push card -----------------------------------------------------

// Three step copies — keyed to which install path applies on the
// current device. Step 3 is identical across devices ("turn on alerts
// from Profile → Settings → Notifications") because that's the in-app
// path once they're standalone. Steps 1 & 2 differ because the install
// flow is platform-specific.
//
// iOS Safari: there's no programmatic install API; the user has to
// open the Share sheet and pick "Add to Home Screen".
// Android Chrome: install lives behind the ⋮ menu as "Install app".
// Other (desktop / unknown browser): we fall back to a generic
// "add to your home screen" line so the card still parses.
const PUSH_STEPS_IOS = [
  ['1', 'Go to <b>triviana.xyz</b> in <b>Safari</b>', 'Tap <b>Share</b> → <b>Add to Home Screen</b>'],
  ['2', 'Open Triviana', 'From the new <b>home-screen icon</b>'],
  ['3', 'Turn on alerts', '<b>Profile → Settings → Notifications</b>'],
];
const PUSH_STEPS_ANDROID = [
  ['1', 'Go to <b>triviana.xyz</b> in <b>Chrome</b>', 'Tap <b>⋮</b> → <b>Install app</b>'],
  ['2', 'Open Triviana', 'Launch it from your <b>home screen</b>'],
  ['3', 'Turn on alerts', '<b>Profile → Settings → Notifications</b>'],
];
const PUSH_STEPS_GENERIC = [
  ['1', 'Go to <b>triviana.xyz</b>', 'Add Triviana to your home screen'],
  ['2', 'Open Triviana', 'From the new home-screen icon'],
  ['3', 'Turn on alerts', '<b>Profile → Settings → Notifications</b>'],
];

function _pickPushSteps() {
  // Already a PWA → step 3 is the only remaining action. Returning
  // a single-row list keeps the card compact for users who only need
  // the in-app toggle.
  if (isStandalonePWA()) return [PUSH_STEPS_GENERIC[2]];
  if (isIOS()) return PUSH_STEPS_IOS;
  if (isAndroid()) return PUSH_STEPS_ANDROID;
  return PUSH_STEPS_GENERIC;
}

function _renderPushSteps(steps) {
  return steps
    .map(
      ([n, title, sub]) => `
        <div class="row" style="align-items:flex-start;gap:10px;margin-top:8px;">
          <div style="flex-shrink:0;width:22px;height:22px;border-radius:50%;background:#F2C879;color:#3a2a00;font-family:var(--font-display);font-weight:800;font-size:12px;display:flex;align-items:center;justify-content:center;line-height:1;">
            ${n}
          </div>
          <div class="grow" style="min-width:0;">
            <div style="color:#fff;font-weight:600;font-size:12.5px;line-height:1.3;">${title}</div>
            <div style="color:#A9A3C4;font-weight:500;font-size:11px;line-height:1.4;margin-top:2px;">${sub}</div>
          </div>
        </div>
      `,
    )
    .join('');
}

function enablePushCardHtml() {
  // Surface the card whenever the user is NOT already opted in. The
  // step list inside the card is device-specific: iOS Safari → Share
  // → Add to Home Screen; Android Chrome → ⋮ → Install app; everyone
  // else → generic "add to home screen" copy. Users who are already
  // running standalone only see step 3 ("Profile → Settings →
  // Notifications").
  const perm = permissionState();
  if (perm === 'granted') return '';
  const supported = isPushSupported();
  const isDenied = perm === 'denied';

  let heading;
  let sub;
  let buttonLabel;
  let buttonAction;
  if (isDenied) {
    heading = 'Push is disabled for Triviana';
    sub = 'Re-enable in your phone/browser settings under Notifications.';
    buttonLabel = 'Open settings instructions';
    buttonAction = 'settings';
  } else if (!supported) {
    // Most common: iOS Safari outside standalone, or a desktop user.
    // The device-specific step list below tells the user how to fix
    // it; the button repeats the guidance via a toast for anyone who
    // skipped past the body text.
    heading = 'Get the ding. Be first in.';
    sub = 'Pin Triviana to your home screen — takes 30 seconds.';
    buttonLabel = 'How to enable';
    buttonAction = 'settings';
  } else {
    heading = 'Turn on push notifications';
    sub = 'Get alerts even when the app is closed';
    buttonLabel = 'Enable notifications';
    buttonAction = 'enable';
  }
  const buttonAttrs = `data-action="${escape(buttonAction)}"`;
  return `
    <div id="notif-enable-push-card" class="card"
         style="border-color:#3A2F5F;background:linear-gradient(135deg,#241C44,#1A1530);">
      <div class="row">
        <div class="ic" style="background:var(--violet-soft);color:#B7A6FF;">
          ${icon(I.bell, { size: 18 })}
        </div>
        <div class="grow">
          <b style="font-size:13.5px;color:#fff;font-weight:600;">
            ${escape(heading)}
          </b>
          <div style="font-size:11px;color:#A9A3C4;font-weight:500;margin-top:1px;">
            ${escape(sub)}
          </div>
        </div>
      </div>
      <div style="margin-top:10px;">
        ${_renderPushSteps(_pickPushSteps())}
      </div>
      <button id="notif-enable-push-btn" type="button" class="btn"
              ${buttonAttrs}
              style="margin-top:12px;margin-bottom:2px;">${escape(buttonLabel)}</button>
    </div>
  `;
}

function wireEnablePush() {
  const btn = document.getElementById('notif-enable-push-btn');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    if (btn.dataset.action === 'settings') {
      // Catches both the denied + unsupported (iOS non-PWA) cases —
      // the 3 steps in the card body already spell out the exact
      // fix; this just nudges the user back to them.
      toast.info(
        'Follow the 3 steps above to enable notifications on this device.',
        { timeout: 6000 },
      );
      return;
    }
    btn.disabled = true;
    btn.textContent = 'Enabling…';
    const res = await requestPermissionAndSubscribe();
    btn.disabled = false;
    if (res.ok) {
      toast.success('Push notifications are on.');
      // Hide the card; permission is granted now.
      const slot = document.getElementById('notif-enable-push-slot');
      if (slot) slot.innerHTML = '';
      return;
    }
    // Reasons: 'denied', 'default' (user dismissed), 'unsupported',
    // 'no_sub', 'sync_failed'. Map to friendlier copy.
    btn.textContent = 'Enable notifications';
    switch (res.reason) {
      case 'denied':
        toast.warn('Push was blocked. Re-enable in your device settings.');
        // Re-render the slot so the "denied" state appears.
        renderEnablePushSlot();
        break;
      case 'unsupported':
        toast.info('Push isn’t supported in this browser. Add Triviana to your home screen first.');
        break;
      case 'sync_failed':
        toast.error('Could not register this device. Try again.');
        break;
      default:
        // 'default' (dismissed) — silent, the user just bailed.
        break;
    }
  });
}

function renderEnablePushSlot() {
  const slot = document.getElementById('notif-enable-push-slot');
  if (!slot) return;
  slot.innerHTML = enablePushCardHtml();
  wireEnablePush();
}
